import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Pagination,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Link,
  Stack,
} from '@mui/material';
import { 
  Visibility as ViewIcon, 
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface AdvertisingInquiry {
  id: number;
  user_id: number | null;
  contact_name: string;
  company_email: string;
  website_url: string;
  company_name: string;
  campaign_date: string;
  advertising_budget: string;
  advert_text: string;
  comments_questions: string;
  created_at: string;
  updated_at: string;
}

interface UpdateAdvertisingInquiryRequest {
  contact_name: string;
  company_email: string;
  website_url: string;
  company_name: string;
  campaign_date: string;
  advertising_budget: string;
  advert_text: string;
  comments_questions: string;
}

const AdvertisingInquiryTab: React.FC = () => {
  const [inquiries, setInquiries] = useState<AdvertisingInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const searchTermRef = useRef(searchTerm);
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editInquiry, setEditInquiry] = useState<AdvertisingInquiry | null>(null);
  const [editForm, setEditForm] = useState<UpdateAdvertisingInquiryRequest>({
    contact_name: '',
    company_email: '',
    website_url: '',
    company_name: '',
    campaign_date: '',
    advertising_budget: '',
    advert_text: '',
    comments_questions: ''
  });
  
  // View details dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewInquiry, setViewInquiry] = useState<AdvertisingInquiry | null>(null);

  // Update ref when searchTerm changes
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    setPage(1); // Reset to first page when submitting search
    fetchInquiries();
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const fetchInquiries = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      if (searchTermRef.current.trim()) {
        params.append('search', searchTermRef.current.trim());
      }
      
      const queryString = params.toString();
      console.log('Advertising Inquiry Search params:', queryString);
      console.log('Advertising Inquiry Search term:', searchTermRef.current);
      
      const response = await api.get(`/api/inquiry/advertising?${queryString}`);
      console.log('Advertising Inquiry Response:', response.data);
      
      // Ensure inquiries is always an array
      const inquiriesData = response.data.data.inquiries || [];
      setInquiries(inquiriesData);
      setTotalPages(response.data.data.pagination.total_pages);
      setError(null);
    } catch (err) {
      console.error('Advertising Inquiry Search error:', err);
      setError('Failed to fetch advertising inquiries');
      setInquiries([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleEditSubmit = async () => {
    if (!editInquiry) return;
    
    try {
      const response = await api.put(`/api/inquiry/advertising/${editInquiry.id}`, editForm);
      
      setError(null);
      setEditDialogOpen(false);
      fetchInquiries(); // Refresh the list
    } catch (err: any) {
      if (err.response?.data?.details) {
        setError(`Failed to update inquiry: ${err.response.data.details}`);
      } else if (err.response?.data?.error) {
        setError(`Failed to update inquiry: ${err.response.data.error}`);
      } else {
        setError(`Failed to update inquiry: ${err.message}`);
      }
    }
  };

  const openEditDialog = (inquiry: AdvertisingInquiry) => {
    setEditInquiry(inquiry);
    setEditForm({
      contact_name: inquiry.contact_name,
      company_email: inquiry.company_email,
      website_url: inquiry.website_url,
      company_name: inquiry.company_name,
      campaign_date: inquiry.campaign_date,
      advertising_budget: inquiry.advertising_budget,
      advert_text: inquiry.advert_text,
      comments_questions: inquiry.comments_questions
    });
    setEditDialogOpen(true);
  };

  const openViewDialog = (inquiry: AdvertisingInquiry) => {
    setViewInquiry(inquiry);
    setViewDialogOpen(true);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };



  // Group inquiries by contact name and company to show duplicates more clearly
  const groupedInquiries = inquiries.reduce((groups, inquiry) => {
    const key = `${inquiry.contact_name}-${inquiry.company_name}`;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(inquiry);
    return groups;
  }, {} as Record<string, AdvertisingInquiry[]>);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Advertising Inquiries
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Manage advertising inquiries and campaign requests from potential clients.
      </Typography>
      
      {Object.keys(groupedInquiries).length < inquiries.length && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Some inquiries appear multiple times due to repeated submissions. 
            Duplicate entries are grouped together and marked with numbers.
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search Bar */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          sx={{ flexGrow: 1 }}
          variant="outlined"
          placeholder="Search by contact name, company name, email, website, or text..."
          value={searchTerm}
          onChange={handleSearch}
          onKeyPress={handleSearchKeyPress}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: (
              <IconButton 
                onClick={handleSearchSubmit}
                size="small"
                sx={{ mr: 1 }}
                title="Search"
              >
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Contact Name</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Campaign Date</TableCell>
              <TableCell>Budget</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inquiries && inquiries.length > 0 ? (
              Object.entries(groupedInquiries).map(([key, groupInquiries]) => (
                <React.Fragment key={key}>
                  {groupInquiries.map((inquiry, index) => (
                    <TableRow 
                      key={inquiry.id}
                      sx={{
                        backgroundColor: index === 0 ? 'inherit' : 'rgba(0, 0, 0, 0.02)',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2">
                          {inquiry.contact_name}
                          {index > 0 && (
                            <Chip 
                              label={`${index + 1}`} 
                              size="small" 
                              sx={{ ml: 1, fontSize: '0.7rem' }}
                            />
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {inquiry.company_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {inquiry.company_email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {inquiry.campaign_date || 'Not specified'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {inquiry.advertising_budget || 'Not specified'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(inquiry.created_at).toLocaleDateString()}
                          {index > 0 && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              {new Date(inquiry.created_at).toLocaleTimeString()}
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => openViewDialog(inquiry)} size="small" title="View Details">
                          <ViewIcon />
                        </IconButton>
                        <IconButton onClick={() => openEditDialog(inquiry)} size="small" title="Edit Inquiry">
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {error ? `Error: ${error}` : 'No advertising inquiries found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Advertising Inquiry</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Name"
                  value={editForm.contact_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, contact_name: e.target.value }))}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Email"
                  value={editForm.company_email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, company_email: e.target.value }))}
                  margin="normal"
                  type="email"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={editForm.company_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, company_name: e.target.value }))}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website URL"
                  value={editForm.website_url}
                  onChange={(e) => setEditForm(prev => ({ ...prev, website_url: e.target.value }))}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Campaign Date"
                  value={editForm.campaign_date}
                  onChange={(e) => setEditForm(prev => ({ ...prev, campaign_date: e.target.value }))}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Advertising Budget"
                  value={editForm.advertising_budget}
                  onChange={(e) => setEditForm(prev => ({ ...prev, advertising_budget: e.target.value }))}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Advert Text"
                  value={editForm.advert_text}
                  onChange={(e) => setEditForm(prev => ({ ...prev, advert_text: e.target.value }))}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comments/Questions"
                  value={editForm.comments_questions}
                  onChange={(e) => setEditForm(prev => ({ ...prev, comments_questions: e.target.value }))}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Inquiry Details</DialogTitle>
        <DialogContent>
          {viewInquiry && (
            <Box>
              <Typography variant="h6" gutterBottom>Contact Information</Typography>
              <Typography><strong>Name:</strong> {viewInquiry.contact_name}</Typography>
              <Typography><strong>Email:</strong> {viewInquiry.company_email}</Typography>
              <Typography><strong>Company:</strong> {viewInquiry.company_name || 'N/A'}</Typography>
              <Typography><strong>Website:</strong> {viewInquiry.website_url || 'N/A'}</Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Campaign Details</Typography>
              <Typography><strong>Campaign Date:</strong> {viewInquiry.campaign_date || 'Not specified'}</Typography>
              <Typography><strong>Budget:</strong> {viewInquiry.advertising_budget || 'Not specified'}</Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Advert Text</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {viewInquiry.advert_text || 'No advert text provided'}
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Comments/Questions</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {viewInquiry.comments_questions || 'No comments or questions'}
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Timestamps</Typography>
              <Typography><strong>Created:</strong> {new Date(viewInquiry.created_at).toLocaleString()}</Typography>
              <Typography><strong>Updated:</strong> {new Date(viewInquiry.updated_at).toLocaleString()}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvertisingInquiryTab;
